
import { createContext, useCallback, useEffect, useState, ReactNode } from "react";
import cookies from "react-cookies";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useLazyQuery } from "@apollo/client";
import { GET_USER } from "./requests";
import { message } from "antd";
import { UserType } from "../../types/user";

interface IUserContext {
  user: UserType | null; // Initially, user can be null
  getUser: () => void;
  isLogged: boolean;
  setIsLogged: (value: boolean) => void;
  userRefetch: () => void;
  logout: () => void;
}

// ? FIX de tipado
const defaultUserContext: IUserContext = {
  user: null,
  getUser: () => {},
  isLogged: false,
  setIsLogged: () => {},
  userRefetch: () => {},
  logout: () => {},
};

const UserContext = createContext<IUserContext>(defaultUserContext);

interface UserProviderProps {
  children: ReactNode;
}

interface CustomJwtPayload extends JwtPayload {
  id: string;
}

const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [isLogged, setIsLogged] = useState(false);

  const [
    userQuery,
    { data: userData, error: userError, refetch: userRefetch },
  ] = useLazyQuery(GET_USER);

  const getUser = useCallback(() => {
    const token = cookies.load("token");
    const tokenPayload = token ? jwtDecode<CustomJwtPayload>(token) : null;

    if (tokenPayload && typeof tokenPayload !== "string" && tokenPayload.id) {
      userQuery({
        variables: {
          id: tokenPayload.id,
        },
        context: {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      });

      if (userError) {
        message.error("Ha habido un error cargando el perfil!");
        logout();
      }

      if (userData) {
        setUser(userData.user);
      }
    } else {
      logout();
    }
  }, [userData, userError, userQuery]);

  const logout = async () => {
    cookies.remove("token");
    setUser(null);
    setIsLogged(false);
  };

  useEffect(() => {
    getUser();
  }, [getUser]);

  return (
    <UserContext.Provider
      value={{
        user,
        getUser,
        isLogged,
        setIsLogged,
        userRefetch,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext };
export default UserProvider;
